import { KeyboardArrowDown, KeyboardArrowUp, Search } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AddIcon, CrossIcon } from 'src/pages/therapist-dashboard/dashboard-icon';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import ConfirmationNormalPopupModal from '../common-modals/delete-modal-normal';
import { TrashIcon } from './commonComponent/therapy-planning-icons';
import TherapyPlanningTemplateSheet from './therapy-planning-template-sheet';
import _ from 'lodash';
import { templateData } from './commonComponent/dummyTemplateData';
import {
  createNewTherapyTemplateAction,
  deleteTherapyTemplateAction,
  getAllTherapyPlanningTemplates,
  resetcreateNewTherapyTemplateAction,
  resetDeleteTherapyTemplateAction,
  editTherapyTemplateAction,
  resetEditTherapyTemplateAction,
  resetGetAllTherapyTemplates,
} from 'src/redux/actions/therapyPlanning';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';

type Props = {
  close: any;
};

type TemplateData = {
  [key: string]: {
    index: number;
    minutes: number;
    type: string;
  }[];
};

type rawData = {
  [day: string]: {
    GROUP: number;
    CONCURRENT: number;
    INDIVIDUAL: number;
  };
};

type MinuteType = 'GROUP' | 'CONCURRENT' | 'INDIVIDUAL';

function convertToTemplate(rawData: rawData) {
  if (!rawData) return;
  const result: TemplateData = {
    INDIVIDUAL: [],
    GROUP: [],
    CONCURRENT: [],
  };

  let i = 0;
  for (const dayData of Object.values(rawData)) {
    if (i > 6) break;
    result.INDIVIDUAL.push({
      index: i,
      minutes: dayData.INDIVIDUAL,
      type: 'individual',
    });
    result.GROUP.push({
      index: i,
      minutes: dayData.GROUP,
      type: 'group',
    });
    result.CONCURRENT.push({
      index: i,
      minutes: dayData.CONCURRENT,
      type: 'concurrent',
    });
    i++;
  }

  return result;
}

function convertToPayload(templateData: TemplateData): rawData {
  const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  const mappedMinutes: rawData = {};

  days.forEach((day) => {
    mappedMinutes[day] = {
      GROUP: 0,
      CONCURRENT: 0,
      INDIVIDUAL: 0,
    };
  });

  for (const [type, entries] of Object.entries(templateData)) {
    const upperType = type.toUpperCase() as MinuteType;
    if (['GROUP', 'CONCURRENT', 'INDIVIDUAL'].includes(upperType)) {
      entries.forEach((entry) => {
        const day = days[entry.index];
        mappedMinutes[day][upperType] = Number(entry.minutes);
      });
    }
  }

  return mappedMinutes;
}

const ManageTherapyPlanningTemplate = (props: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const { close } = props;
  const [anchorEl1, setAnchorEl1] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl1);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTempl, setSelectedTempl] = useState<any>();
  const [templateName, setTemplateName] = useState('');
  const [templates, setTemplates] = useState<
    { uuid: string; templateName: string; content: any }[]
  >([]);

  const [tempMode, setTempMode] = useState<'VIEW' | 'EDIT' | 'CREATE' | ''>('VIEW');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [popUpMessage, setPopUpMessage] = useState({
    message: '',
    type: '',
  });

  const getAllTemplatesList = useSelector(
    (state: any) => state.getAllTherapyPlaningTemplatesReducer
  );

  const createTemplateResponse = useSelector((state: any) => state.createNewTherapyTemplateReducer);
  const deleteTemplateReponse = useSelector((state: any) => state.deleteTherapyTemplateReducer);
  const editTemplateReponse = useSelector((state: any) => state.editTherapyTemplateReducer);

  const handleClickMenu = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl1(event.currentTarget);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setSearchTerm('');
    setAnchorEl1(null);
  }, []);

  const handleSelectedTemplate = useCallback((therapist: any) => {
    setSelectedTempl({
      templateName: therapist?.templateName,
      uuid: therapist?.uuid,
      template: convertToTemplate(therapist?.content),
    });
    setAnchorEl1(null);
  }, []);

  const handleCloseDialog = () => {
    close();
  };

  const handleSaveEditAction = () => {
    if (tempMode === 'VIEW') {
      setTempMode('EDIT');
      setTemplateName(selectedTempl?.templateName);
    } else if (tempMode === 'CREATE') {
      const payloadData = convertToPayload(selectedTempl.template);
      dispatch(
        createNewTherapyTemplateAction({ templateName: templateName, content: payloadData })
      );
    } else {
      const payloadData = convertToPayload(selectedTempl.template);
      dispatch(
        editTherapyTemplateAction({
          uuid: selectedTempl.uuid,
          templateName: templateName,
          content: payloadData,
        })
      );
    }
  };

  const handleDeleteSelectedTemplate = () => {
    setTempMode('VIEW');
    setTemplates([]);
    dispatch(
      deleteTherapyTemplateAction({
        uuid: selectedTempl?.uuid,
      })
    );
    setOpenDeleteModal(false);
  };

  const handleCloseTempDelete = () => {
    setOpenDeleteModal(false);
  };

  const handleScroll = () => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10 && !getAllTemplatesList?.loading) {
      if (
        getAllTemplatesList?.response?.data?.totalElements !==
        getAllTemplatesList?.totalTemplates?.length
      ) {
        dispatch(
          getAllTherapyPlanningTemplates({
            params: {
              page: getAllTemplatesList?.pageNumber,
              pageSize: 20,
            },
          })
        );
      }
    }
  };

  const returnSelectedTemplate = (uuid: string) => {
    const template = templates.find((temp: any) => temp?.uuid === uuid);
    return {
      uuid: uuid,
      templateName: template?.templateName,
      template: convertToTemplate(template?.content),
    };
  };

  useEffect(() => {
    const debounceSearch = _.debounce(() => {
      dispatch(resetGetAllTherapyTemplates());
      dispatch(
        getAllTherapyPlanningTemplates({
          params: {
            page: 0,
            pageSize: 20,
            templateName: searchTerm?.trim(),
          },
        })
      );
    }, 1000);

    debounceSearch();

    return () => debounceSearch.cancel();
  }, [searchTerm]);

  useEffect(() => {
    if (getAllTemplatesList?.totalTemplates?.length) {
      setTemplates(getAllTemplatesList?.totalTemplates);
      const firstTemplate = getAllTemplatesList?.totalTemplates?.[0];
      const temp = {
        uuid: firstTemplate?.uuid,
        templateName: firstTemplate?.templateName,
        template: convertToTemplate(firstTemplate?.content),
      };
      setTempMode('VIEW');
      if (selectedTempl) return;
      setSelectedTempl(temp);
    } else {
      !templates?.length && setTempMode('');
    }
  }, [getAllTemplatesList]);

  useEffect(() => {
    if (createTemplateResponse?.response === null) return;

    if (
      createTemplateResponse?.response !== null &&
      createTemplateResponse?.response?.code !== 'OK'
    ) {
      setPopUpMessage({ message: createTemplateResponse?.response?.message, type: 'ERROR' });
      setTimeout(() => {
        setPopUpMessage({
          message: '',
          type: '',
        });
      }, 3000);
      dispatch(resetcreateNewTherapyTemplateAction());
      return;
    }

    setSelectedTempl('');
    dispatch(resetGetAllTherapyTemplates());
    dispatch(
      getAllTherapyPlanningTemplates({
        params: {
          page: 0,
          pageSize: 20,
        },
      })
    );
    setTempMode('VIEW');
    setPopUpMessage({ message: createTemplateResponse?.response?.message, type: '' });
    setTimeout(() => {
      setPopUpMessage({
        message: '',
        type: '',
      });
    }, 3000);
    dispatch(resetcreateNewTherapyTemplateAction());
  }, [createTemplateResponse]);

  useEffect(() => {
    if (editTemplateReponse?.response === null) return;

    if (editTemplateReponse?.response !== null && editTemplateReponse?.response?.code !== 'OK') {
      setPopUpMessage({ message: editTemplateReponse?.response?.message, type: 'ERROR' });
      setTimeout(() => {
        setPopUpMessage({
          message: '',
          type: '',
        });
      }, 3000);
      dispatch(resetEditTherapyTemplateAction());
      return;
    }

    // setSelectedTempl('');
    dispatch(resetGetAllTherapyTemplates());
    dispatch(
      getAllTherapyPlanningTemplates({
        params: {
          page: 0,
          pageSize: 20,
        },
      })
    );
    setSelectedTempl((prev: any) => ({
      ...prev,
      templateName: templateName,
    }));

    setTempMode('VIEW');
    setPopUpMessage({ message: editTemplateReponse?.response?.message, type: '' });
    setTimeout(() => {
      setPopUpMessage({
        message: '',
        type: '',
      });
    }, 3000);
    dispatch(resetEditTherapyTemplateAction());
  }, [editTemplateReponse]);

  useEffect(() => {
    if (deleteTemplateReponse?.response === null) return;

    if (
      deleteTemplateReponse?.response !== null &&
      deleteTemplateReponse?.response?.code !== 'OK'
    ) {
      setPopUpMessage({ message: deleteTemplateReponse?.response?.message, type: 'ERROR' });
      setTimeout(() => {
        setPopUpMessage({
          message: '',
          type: '',
        });
      }, 3000);
      dispatch(resetDeleteTherapyTemplateAction());
      return;
    }

    setSelectedTempl('');
    dispatch(resetGetAllTherapyTemplates());
    dispatch(
      getAllTherapyPlanningTemplates({
        params: {
          page: 0,
          pageSize: 20,
        },
      })
    );
    setPopUpMessage({ message: deleteTemplateReponse?.response?.message, type: '' });
    setTimeout(() => {
      setPopUpMessage({
        message: '',
        type: '',
      });
    }, 3000);
    dispatch(resetDeleteTherapyTemplateAction());
  }, [deleteTemplateReponse]);

  return (
    <>
      <Box padding={1}>
        <DialogContent>
          <Stack
            flexDirection={'column'}
            rowGap={2}
          >
            <Stack
              sx={{ display: 'flex', textAlign: 'center' }}
              mb={1}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  gap={'10px'}
                >
                  <Typography variant="h5">Therapy Templates</Typography>
                  <Chip
                    label={`${getAllTemplatesList?.response?.data?.totalElements || 0} Templates`}
                    variant="outlined"
                    sx={{ marginLeft: '10px', border: '1px solid #afb3ba9e' }}
                  />
                </Box>
                <IconButton onClick={handleCloseDialog}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Stack>
            {tempMode === 'VIEW' ? (
              <Stack>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  {
                    <Box
                      gap={1}
                      display={'flex'}
                      alignItems={'center'}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'Plus Jakarta Sans',
                          fontSize: '18px',
                          fontStyle: 'normal',
                          fontWeight: 700,
                          lineHeight: '21.6px',
                          textWrap: 'nowrap',
                        }}
                      >
                        Select a Template
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                          padding: '6px 16px',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Button
                          id="basic-button"
                          onClick={handleClickMenu}
                          sx={{
                            borderColor: '#afb3ba9e',
                            color: '#111927',
                            width: '500px',
                            height: '34px',
                            justifyContent: 'space-between',
                          }}
                          variant="outlined"
                          endIcon={open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        >
                          <Typography
                            sx={{
                              width: 400,
                              fontWeight: 400,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              textAlign: 'left',
                            }}
                          >
                            {selectedTempl?.templateName || 'No templates available'}
                          </Typography>
                        </Button>
                      </Box>
                    </Box>
                  }

                  <Box>
                    <Button
                      variant="text"
                      startIcon={<AddIcon />}
                      sx={{
                        color: '#6366F1',
                        margin: '0px',
                        padding: '0px',
                        paddingBottom: '2px',
                        cursor: 'pointer',
                        borderRadius: 'unset',
                        backgroundColor: 'transparent',
                        width: 'fit-content',
                      }}
                      onClick={() => {
                        setTempMode('CREATE');
                        setSelectedTempl({
                          templateName: '',
                          template: templateData,
                        });
                        setTemplateName('');
                      }}
                    >
                      Create Template
                    </Button>
                  </Box>
                </Box>

                <Box>
                  <TherapyPlanningTemplateSheet
                    viewMode={tempMode === 'VIEW'}
                    therapyTemplateData={selectedTempl}
                    setTherapyTemplateData={setSelectedTempl}
                    // templatesLoading={getAllTemplatesList?.loading && !templates?.length}
                  />
                </Box>
              </Stack>
            ) : tempMode === 'CREATE' ? (
              <Stack
                flexDirection={'column'}
                rowGap={2}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '24px',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Plus Jakarta Sans',
                      fontSize: '18px',
                      fontStyle: 'normal',
                      fontWeight: 700,
                      lineHeight: '21.6px',
                      textWrap: 'nowrap',
                    }}
                  >
                    Create Template
                  </Typography>

                  <TextField
                    variant="outlined"
                    placeholder={`Template name`}
                    fullWidth
                    onChange={(event: any) => {
                      setTemplateName(event.target.value);
                    }}
                    value={templateName}
                  />
                </Box>
                <Box>
                  <TherapyPlanningTemplateSheet
                    therapyTemplateData={selectedTempl}
                    setTherapyTemplateData={setSelectedTempl}
                    // templatesLoading={false}
                  />
                </Box>
              </Stack>
            ) : tempMode === 'EDIT' ? (
              <Stack
                flexDirection={'column'}
                rowGap={2}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '24px',
                      width: '60%',
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'Plus Jakarta Sans',
                        fontSize: '18px',
                        fontStyle: 'normal',
                        fontWeight: 700,
                        lineHeight: '21.6px',
                        textWrap: 'nowrap',
                      }}
                    >
                      Edit Template
                    </Typography>
                    <TextField
                      variant="outlined"
                      placeholder={`Template name`}
                      fullWidth
                      onChange={(event: any) => {
                        setTemplateName(event.target.value);
                      }}
                      value={templateName}
                    />
                  </Box>
                  <Box>
                    <Button
                      variant="text"
                      startIcon={<AddIcon />}
                      sx={{
                        color: '#6366F1',
                        margin: '0px',
                        padding: '0px',
                        paddingBottom: '2px',
                        cursor: 'pointer',
                        borderRadius: 'unset',
                        backgroundColor: 'transparent',
                        width: 'fit-content',
                      }}
                      onClick={() => {
                        setTempMode('CREATE');
                      }}
                    >
                      Create Template
                    </Button>
                  </Box>
                </Box>
                <Box>
                  <TherapyPlanningTemplateSheet
                    therapyTemplateData={selectedTempl}
                    setTherapyTemplateData={setSelectedTempl}
                    // templatesLoading={false}
                  />
                </Box>
              </Stack>
            ) : getAllTemplatesList?.response?.data?.totalElements === 0 &&
              !getAllTemplatesList?.loading ? (
              // no templates available case
              <Stack>
                <Box
                  display={'flex'}
                  flexDirection={'row-reverse'}
                >
                  <Button
                    variant="text"
                    startIcon={<AddIcon />}
                    sx={{
                      color: '#6366F1',
                      margin: '0px',
                      padding: '0px',
                      paddingBottom: '2px',
                      cursor: 'pointer',
                      borderRadius: 'unset',
                      backgroundColor: 'transparent',
                      width: 'fit-content',
                    }}
                    onClick={() => {
                      setTemplateName('');
                      setSelectedTempl({
                        templateName: '',
                        template: templateData,
                      });
                      setTempMode('CREATE');
                    }}
                  >
                    Create Template
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '200px',
                  }}
                >
                  <Typography
                    variant="h5"
                    sx={{ marginBottom: 1 }}
                  >
                    No templates yet! Click 'Create template' to
                  </Typography>
                  <Typography variant="h5">build your first template and get started.</Typography>
                </Box>
              </Stack>
            ) : (
              <Box>
                <TherapyPlanningTemplateSheet
                  therapyTemplateData={templateData}
                  setTherapyTemplateData={setSelectedTempl}
                  templatesLoading={true}
                />
              </Box>
            )}
          </Stack>
        </DialogContent>
        {tempMode && (
          <DialogActions sx={{ justifyContent: 'space-between' }}>
            {tempMode !== 'CREATE' ? (
              <IconButton
                onClick={() => setOpenDeleteModal(true)}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  borderRadius: '11.765px',
                  background: 'var(--error-light, #FEE4E2)',
                }}
              >
                <TrashIcon />
              </IconButton>
            ) : (
              <Box></Box>
            )}
            <Box
              sx={{
                display: 'flex',
                gap: '10px',
              }}
            >
              {tempMode !== 'VIEW' && (
                <Button
                  autoFocus
                  variant="text"
                  onClick={() => {
                    templates.length
                      ? (setTempMode('VIEW'),
                        tempMode === 'CREATE'
                          ? setSelectedTempl({
                              uuid: getAllTemplatesList?.totalTemplates?.[0]?.uuid,
                              templateName: getAllTemplatesList?.totalTemplates?.[0]?.templateName,
                              template: convertToTemplate(
                                getAllTemplatesList?.totalTemplates?.[0]?.content
                              ),
                            })
                          : setSelectedTempl(returnSelectedTemplate(selectedTempl?.uuid)))
                      : setTempMode('');
                  }}
                >
                  {'Cancel'}
                </Button>
              )}
              <Button
                autoFocus
                variant="contained"
                type="submit"
                onClick={handleSaveEditAction}
                disabled={tempMode !== 'VIEW' && !templateName}
              >
                {tempMode === 'VIEW' ? 'Edit Template' : 'Save Template'}
              </Button>
            </Box>
          </DialogActions>
        )}
      </Box>

      <Menu
        id="basic-menu"
        anchorEl={anchorEl1}
        open={open}
        onClose={handleCloseMenu}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        <TextField
          variant="outlined"
          placeholder={`Search by template name`}
          fullWidth
          onChange={(event: any) => {
            setSearchTerm(event.target.value);
          }}
          value={searchTerm}
          sx={{
            p: '0px 5px',
            '& .MuiInputBase-input': {
              padding: '12px 0px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment
                position="end"
                onClick={() => setSearchTerm('')}
                sx={{
                  ':hover': {
                    cursor: 'pointer',
                  },
                }}
              >
                <CrossIcon />
              </InputAdornment>
            ),
          }}
        />
        <Box
          ref={containerRef}
          onScroll={handleScroll}
          style={{
            height: '400px',
            overflowY: 'auto',
            width: '500px',
          }}
        >
          {!getAllTemplatesList?.totalTemplates?.length ? (
            <Typography
              color={'text.secondary'}
              variant="subtitle2"
              sx={{
                textAlign: 'center',
                p: 2,
              }}
            >
              No results found
            </Typography>
          ) : (
            getAllTemplatesList?.totalTemplates?.map((template: any) => (
              <MenuItem
                key={template?.uuid}
                onClick={() => handleSelectedTemplate(template)}
                sx={{ display: 'flex', py: 1, justifyContent: 'space-between' }}
              >
                <Typography
                  sx={{
                    ml: 1,
                    overflow: 'hidden',
                    textAlign: 'left',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {`${template?.templateName}`}
                </Typography>
                <IconButton
                  onClick={() => {
                    setOpenDeleteModal(true);
                    setSelectedTempl(template);
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </MenuItem>
            ))
          )}
          {getAllTemplatesList?.loading && (
            <Box
              display="flex"
              justifyContent="center"
              p={2}
            >
              <CircularProgress />
            </Box>
          )}
        </Box>
      </Menu>

      <BootstrapDialog
        onClose={handleCloseTempDelete}
        aria-labelledby="customized-dialog-title"
        open={openDeleteModal}
        PaperProps={{ style: { width: '650px' } }}
      >
        <ConfirmationNormalPopupModal
          title={'Delete Template'}
          message={'Are you sure you want to delete this template?'}
          button={{ saveLabel: `Yes, I'm sure`, closeLabel: 'Cancel' }}
          close={handleCloseTempDelete}
          handleSaveBtn={handleDeleteSelectedTemplate}
        />
      </BootstrapDialog>

      {popUpMessage.message && (
        <SimpleSnackbar
          message={popUpMessage.message}
          isError={popUpMessage.type === 'ERROR'}
          isWarning={popUpMessage.type === 'WARNING'}
        />
      )}
    </>
  );
};

export default ManageTherapyPlanningTemplate;
